import { useState } from 'react';
import { addYears } from 'date-fns';
import classNames from 'classnames';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NSButton, NSDatePicker, NSTable } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatIsoDate, numeralFormatterCurrency, parseIsoDate } from 'ns_libs/formatter';
import { IOtherRevenue, IOtherRevenuePatch } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { YEARS_AFTER_DEV_END } from '../constants';
import './ProFormaOtherRevenue.scss';

const TH_CLASS = 'NSTable__thead__tr__th align-middle py-1 px-3';
const TD_CLASS = 'NSTable__tbody__tr__td align-middle px-3 py-2';
const TFOOT_TD_CLASS = 'NSTable__tfoot__tr__td align-middle px-3 py-2';

interface IOtherRevenueRowProps {
    row: IOtherRevenue;
    minDate?: Date;
    maxDate?: Date;
    onUpdate: (rowId: number, patch: IOtherRevenuePatch) => void;
    onDelete: (rowId: number) => void;
}

const OtherRevenueRow = ({ row, minDate, maxDate, onUpdate, onDelete }: IOtherRevenueRowProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
            <td
                className={`${TD_CLASS} NSTable__tbody__tr__td--right-border`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                        <NSCellInput
                            value={row.description ?? ''}
                            handleUpdateValue={value => onUpdate(row.id, { description: String(value) || null })}
                            placeholder="Enter description (i.e., Impact fee credits)"
                        />
                    </div>
                    <RenderIf isTrue={isHovered}>
                        <div className="d-flex align-items-center ml-2">
                            <NSButton
                                icon={faTrash}
                                size="sm"
                                className={classNames('px-2 text-muted hover:text-dark bg-none hover:bg-light')}
                                callback={() => onDelete(row.id)}
                                dataTestId={`delete-other-revenue-${row.id}`}
                            />
                        </div>
                    </RenderIf>
                </div>
            </td>
            <td className={`${TD_CLASS} text-right`}>
                <NSDatePicker
                    variant="inline"
                    name={`date-${row.id}`}
                    value={row.date ? parseIsoDate(row.date) : null}
                    onChange={(date: Date | null) => onUpdate(row.id, { date: date ? formatIsoDate(date) : null })}
                    showMonthYearPicker
                    dateFormat="MMM yyyy"
                    placeholderText="Enter month date"
                    minDate={minDate}
                    maxDate={maxDate}
                />
            </td>
            <td className={`${TD_CLASS} text-right`}>
                <NSCellInput
                    value={row.amount}
                    handleUpdateValue={value => onUpdate(row.id, { amount: Number(value) })}
                    numberType="currency"
                    showCents
                    placeholder="Enter $ amount"
                    isRightAligned
                />
            </td>
        </tr>
    );
};

const ProFormaOtherRevenue = () => {
    const {
        proForma, uses, otherRevenue,
        handleCreateOtherRevenue, handleUpdateOtherRevenue, handleDeleteOtherRevenue,
    } = useProFormaSocketContext();

    const rows: IOtherRevenue[] = otherRevenue?.otherRevenues ?? [];
    const minDateString = proForma?.constructionStart ?? uses?.developmentStartDate;
    const minDate = minDateString ? parseIsoDate(minDateString) ?? undefined : undefined;
    const devEnd = uses?.developmentEndDate ? parseIsoDate(uses.developmentEndDate) : null;
    const maxDate = devEnd ? addYears(devEnd, YEARS_AFTER_DEV_END) : undefined;
    const totalAmount = rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);

    const updateField = (rowId: number, patch: IOtherRevenuePatch) => {
        if (handleUpdateOtherRevenue) handleUpdateOtherRevenue(rowId, patch);
    };

    const deleteRow = (rowId: number) => {
        if (handleDeleteOtherRevenue) handleDeleteOtherRevenue(rowId);
    };

    return (
        <div className="ProFormaOtherRevenue m-3">
            <NSTable className="NSTable ProFormaOtherRevenue__table mb-0">
                <thead className="NSTable__thead">
                    <tr className="NSTable__thead__tr">
                        <th className={`${TH_CLASS} NSTable__thead__tr__th--right-border`}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center ml-1">
                                    <span className="ml-0">Description</span>
                                </div>
                                <NSButton
                                    className={classNames('px-2 text-muted hover:text-dark bg-none hover:bg-light')}
                                    size="sm"
                                    icon={faPlus}
                                    callback={() => handleCreateOtherRevenue && handleCreateOtherRevenue({})}
                                    dataTestId="add-other-revenue-row"
                                />
                            </div>
                        </th>
                        <th className={`${TH_CLASS} text-right`}>Date</th>
                        <th className={`${TH_CLASS} text-right`}>Amount</th>
                    </tr>
                </thead>
                <tbody className="NSTable__tbody">
                    {rows.map(row => (
                        <OtherRevenueRow
                            key={row.id}
                            row={row}
                            minDate={minDate}
                            maxDate={maxDate}
                            onUpdate={updateField}
                            onDelete={deleteRow}
                        />
                    ))}
                </tbody>
                <tfoot className="NSTable__tfoot">
                    <tr className="NSTable__tfoot__tr">
                        <td className={`${TFOOT_TD_CLASS} NSTable__tfoot__tr__td--right-border text-dark font-weight-bold`}>Total</td>
                        <td className={`${TFOOT_TD_CLASS} text-right`} />
                        <td className={`${TFOOT_TD_CLASS} text-right text-dark font-weight-bold`}>
                            {numeralFormatterCurrency(totalAmount)}
                        </td>
                    </tr>
                </tfoot>
            </NSTable>
        </div>
    );
};

export default ProFormaOtherRevenue;
